import { z } from "zod";
import type { KpiEntryRecord } from "./kpi";
import { GeneratedInsight } from "./insight";

// ---------------------------------------------------------------------------
// Autofill gaps (GRA-59)
//
// A CRM-read KPI the nightly autofill could not produce a value for used to be
// a dead end: an empty, read-only cell that read like a quiet period. A gap
// records why, so the team leader is asked for the number instead, and can say
// they cannot give it this period.
//
// Stored on the team's entry for the period (`gaps` are the job's, `gapsSkipped`
// the leader's), because both are decided per team and period.
// ---------------------------------------------------------------------------

/**
 * - `not_computable`: the CRM data does not answer the KPI's instruction.
 * - `connection`: no connection, or the session to it failed.
 *
 * Sellers not linked to a CRM user are deliberately not a gap: the answer to
 * that is linking them (GRA-56), not typing their numbers in by hand. A value
 * of 0 is an answer, never a gap.
 */
export const KPI_GAP_KINDS = ["not_computable", "connection"] as const;
export const KpiGapKind = z.enum(KPI_GAP_KINDS);
export type KpiGapKind = z.infer<typeof KpiGapKind>;

export const KpiAutofillGap = z.object({
  kind: KpiGapKind,
  toolkitSlug: z.string().min(1),
  /** The gather's one-line reason, when it gave one. */
  note: z.string().nullable().default(null),
  at: z.string(),
});
export type KpiAutofillGap = z.infer<typeof KpiAutofillGap>;

/**
 * Why one linked seller has no automatic value for a per-seller KPI that the
 * job did produce for others (GRA-102). Without it the cell read "–", like a
 * quiet period.
 *
 * - `no_value`: the seller's read ran, but produced no number for the KPI.
 * - `read_failed`: the seller's read failed (the model or the CRM); tomorrow's
 *   run will most likely fill it.
 *
 * Not linked, not on the team and manual values are not misses: the first two
 * have their own answer, and a manual value is a value.
 */
export const KPI_MEMBER_MISS_REASONS = ["no_value", "read_failed"] as const;
export const KpiMemberMissReason = z.enum(KPI_MEMBER_MISS_REASONS);
export type KpiMemberMissReason = z.infer<typeof KpiMemberMissReason>;

export const KpiMemberMiss = z.object({
  reason: KpiMemberMissReason,
  toolkitSlug: z.string().min(1),
  at: z.string(),
});
export type KpiMemberMiss = z.infer<typeof KpiMemberMiss>;

/** Member id → KPI id → miss. Stored on the team entry, next to the gaps. */
export type KpiMemberMisses = Record<string, Record<string, KpiMemberMiss>>;

/** "Kan inte fylla i": the leader declined to fill in this period's gaps. */
export const KpiGapsSkipped = z.object({
  by: z.string().min(1),
  at: z.string(),
});
export type KpiGapsSkipped = z.infer<typeof KpiGapsSkipped>;

/**
 * The KPI ids of one team's period that still need a manual value: the job
 * recorded a gap, nobody has entered a value for that KPI on any of the
 * team's subjects since, and the leader has not skipped the period.
 */
export function openKpiGaps(
  entries: readonly KpiEntryRecord[],
  teamId: string,
  periodStart: string,
): Map<string, KpiAutofillGap> {
  const forTeam = entries.filter(
    (e) => e.teamId === teamId && e.periodStart === periodStart,
  );
  const teamEntry = forTeam.find((e) => e.subjectKind === "team");
  const open = new Map<string, KpiAutofillGap>();
  if (teamEntry == null || teamEntry.gapsSkipped != null) return open;
  for (const [kpiId, gap] of Object.entries(teamEntry.autofillGaps ?? {})) {
    const filled = forTeam.some((e) => typeof e.values[kpiId] === "number");
    if (!filled) open.set(kpiId, gap);
  }
  return open;
}

export const SkipKpiGapsInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1),
  periodStart: z.string().min(1),
});
export type SkipKpiGapsInput = z.infer<typeof SkipKpiGapsInput>;

/** Dedupe key of the one "fill in these KPIs" insight per team and period. */
export const KPI_GAPS_GENERATOR_KEY = "kpi-gaps";
export function kpiGapsInsightKey(teamId: string, periodStart: string): string {
  return `${KPI_GAPS_GENERATOR_KEY}:${teamId}:${periodStart}`;
}

/** Periods in a row a KPI can fail before the prompt suggests changing it. */
export const KPI_GAP_STREAK_TO_SUGGEST_CHANGE = 3;

/**
 * The "fill in these KPIs" prompts: one per team with open gaps in the newest
 * period (`periods` oldest first, the newest being the check-in period the
 * autofill writes to). Keyed per team and period, so the reconcile expires it
 * the night the gaps close or are skipped.
 *
 * When the same KPI could not be worked out in each of the last
 * `KPI_GAP_STREAK_TO_SUGGEST_CHANGE` periods, the prompt says so and points at
 * rewording the instruction or switching it to manual.
 */
export function buildKpiGapInsights(input: {
  entries: readonly KpiEntryRecord[];
  periods: readonly { periodStart: string; periodEnd: string }[];
  teams: readonly { id: string; name: string }[];
  definitions: readonly { id: string; name: string }[];
}): GeneratedInsight[] {
  const current = input.periods[input.periods.length - 1];
  if (current == null) return [];
  const earlier = input.periods.slice(-KPI_GAP_STREAK_TO_SUGGEST_CHANGE, -1);
  const nameOf = new Map(input.definitions.map((d) => [d.id, d.name]));
  const out: GeneratedInsight[] = [];

  for (const team of input.teams) {
    const open = openKpiGaps(input.entries, team.id, current.periodStart);
    const kpiIds = [...open.keys()].filter((id) => nameOf.has(id));
    if (kpiIds.length === 0) continue;

    const names = kpiIds.map((id) => nameOf.get(id)!);
    const stuck = kpiIds.filter(
      (id) =>
        open.get(id)!.kind === "not_computable" &&
        earlier.length === KPI_GAP_STREAK_TO_SUGGEST_CHANGE - 1 &&
        earlier.every((p) => {
          const teamEntry = input.entries.find(
            (e) =>
              e.teamId === team.id &&
              e.periodStart === p.periodStart &&
              e.subjectKind === "team",
          );
          return teamEntry?.autofillGaps?.[id]?.kind === "not_computable";
        }),
    );
    const connection = kpiIds.some((id) => open.get(id)!.kind === "connection");
    const list =
      names.length === 1
        ? names[0]!
        : `${names.slice(0, -1).join(", ")} och ${names[names.length - 1]}`;

    let description =
      `${list} kunde inte hämtas automatiskt för perioden ` +
      `${current.periodStart} till ${current.periodEnd}. ` +
      `Fyll i värdena manuellt, eller markera att de inte kan fyllas i.`;
    if (connection) {
      description +=
        " Anslutningen till CRM:et fungerar inte, be en administratör ansluta igen.";
    }
    if (stuck.length > 0) {
      description +=
        ` ${stuck.map((id) => nameOf.get(id)).join(", ")} har inte gått att räkna fram ` +
        `${KPI_GAP_STREAK_TO_SUGGEST_CHANGE} perioder i rad. Formulera om instruktionen ` +
        `eller gör nyckeltalet manuellt.`;
    }

    out.push(
      GeneratedInsight.parse({
        generatorKey: KPI_GAPS_GENERATOR_KEY,
        dedupeKey: kpiGapsInsightKey(team.id, current.periodStart),
        source: "rule",
        scope: "team",
        salesTeamId: team.id,
        severity: "warning",
        accent: "warning",
        ctaVariant: "button",
        icon: "ClipboardList",
        title:
          names.length === 1
            ? `Fyll i ${names[0]} för ${team.name}`
            : `Fyll i ${names.length} nyckeltal för ${team.name}`,
        description,
        cta: {
          label: "Fyll i nu",
          to: `/coach/performance/pulse?team=${encodeURIComponent(team.id)}&periodStart=${current.periodStart}&periodEnd=${current.periodEnd}`,
        },
        priority: 70,
        confidence: 1,
        evidence: kpiIds.map((id) => {
          const gap = open.get(id)!;
          return gap.note
            ? `${nameOf.get(id)}: ${gap.note}`
            : `${nameOf.get(id)}: ${gap.kind}`;
        }),
      }),
    );
  }
  return out;
}
